import { useState } from 'react'
import { useFormik } from 'formik'
import type { InviteeInput } from '@sportninja/common/services/teamBalance'
import type { InviteFormValues, InviteRow } from '../types/slotInvite'
import { guardMessage } from '../utils/balanceError'
import { balanceText } from '../utils/balanceI18n'
import {
  initialInviteValues,
  mapInviteServerErrors,
  toInvitees,
  validateInviteRows,
} from '../utils/inviteForm'

interface UseInviteeFormOptions {
  /** Who the slot is held for now (Change Invitee). Every other form starts from one empty row. */
  initialInvitee?: Partial<InviteRow> | null
  /** Resolves on success (the caller closes); rejects with the API error. */
  onSubmit: (invitees: InviteeInput[]) => Promise<unknown>
}

/**
 * Jeff, Oct 1 — the invite form's state, shared by Add Player Slots › Invite
 * Players (one new slot per row) and the ⋮ Invite Player / Change Invitee form
 * (one row).
 *
 * The rows are checked here first (`inviteForm`); the server's own refusals land
 * on the row and field they name, and anything that isn't about a field (the
 * budget is locked, the team isn't approved) comes back as `formMessage`, for the
 * form to show above the rows.
 */
export const useInviteeForm = ({
  initialInvitee = null,
  onSubmit,
}: UseInviteeFormOptions) => {
  'use memo'
  const [formMessage, setFormMessage] = useState<string | null>(null)

  const messages = {
    firstNameRequired: balanceText('common:balanceInviteErrorFirstName'),
    lastNameRequired: balanceText('common:balanceInviteErrorLastName'),
    emailRequired: balanceText('common:balanceInviteErrorEmail'),
    emailInvalid: balanceText('common:balanceInviteErrorEmailInvalid'),
    duplicate: balanceText('common:balanceInviteErrorDuplicate'),
  }

  const formik = useFormik<InviteFormValues>({
    initialValues: initialInviteValues(initialInvitee),
    validate: (values) => validateInviteRows(values, messages),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, helpers) => {
      setFormMessage(null)
      try {
        await onSubmit(toInvitees(values))
      } catch (error) {
        const { fieldErrors, formMessage: serverMessage } =
          mapInviteServerErrors(
            (error as { invalid_fields?: Record<string, unknown> } | null)
              ?.invalid_fields
          )
        Object.entries(fieldErrors).forEach(([name, message]) =>
          helpers.setFieldError(name, message)
        )
        setFormMessage(
          serverMessage ??
            (Object.keys(fieldErrors).length > 0 ? null : guardMessage(error))
        )
      }
    },
  })

  return { formik, formMessage }
}

export default useInviteeForm
